import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ChevronRight, Check, Star, ArrowRight } from 'lucide-react';

export default function Landing() {
  const navigate = useNavigate();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [billingCycle, setBillingCycle] = useState('monthly');

  const testimonials = [
    {
      name: "Marcus Johnson",
      role: "Tech Entrepreneur, Atlanta",
      quote: "I've never experienced anything like Pivot To Power Network. The clarity I gained transformed my entire approach to the AI economy. I finally know my path forward with absolute certainty."
    },
    {
      name: "Dr. Amara Williams",
      role: "Career Strategist, New York",
      quote: "This network gave me what I couldn't find anywhere else - strategic clarity, elite community, and practical tools. I went from confusion to complete confidence in 30 days."
    },
    {
      name: "David Thompson",
      role: "AI Consultant, Chicago",
      quote: "The level of strategic guidance here is unprecedented. Power Guide AI and the community helped me pivot from uncertainty to a clear, profitable roadmap."
    },
    {
      name: "Jasmine Carter",
      role: "Business Owner, Houston",
      quote: "Joining this exclusive network was the turning point. I have clarity on what to do, community support, and the confidence to thrive in the age of AI."
    },
    {
      name: "Kenneth Roberts",
      role: "Product Manager, San Francisco",
      quote: "The diagnostic revealed insights I didn't know I needed. Combined with the AI guidance and Power Circles, I'm building the future I always wanted."
    },
    {
      name: "Nia Jackson",
      role: "Marketing Director, Miami",
      quote: "This is the most valuable investment I've made in my career. The exclusive community, strategic tools, and personalized roadmap changed everything."
    }
  ];

  const successStories = [
    {
      name: "Sarah Martinez",
      title: "From Corporate Layoff to AI Business Owner",
      result: "Built $250K/year AI consulting practice in 6 months",
      quote: "The Pivot To Power diagnostic helped me discover my unique archetype. With the community's support and Power Guide AI, I transformed my layoff into the best thing that ever happened to me."
    },
    {
      name: "James Anderson",
      title: "From Confused to Clear Career Direction",
      result: "Landed dream role at leading AI company",
      quote: "I was paralyzed by too many options. The Power Path Finder gave me crystal-clear direction. Within 90 days, I secured a role I didn't think was possible."
    },
    {
      name: "Michelle Davis",
      title: "From Side Hustle to Full-Time Creator",
      result: "Scaled to $15K/month in creator income",
      quote: "The Lifestyle Designer tool helped me map out exactly what I needed to earn and how to get there. The accountability from my Power Circle made it real."
    },
    {
      name: "Robert Chen",
      title: "From Tech Worker to Strategic Leader",
      result: "Promoted to VP within one year",
      quote: "The AI readiness training and strategic frameworks from the Academy positioned me as the go-to leader for AI transformation at my company."
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-amber-500/20" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-8 md:px-16 py-5 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_clarity-network-1/artifacts/b1jdxi6j_image.png" 
              alt="Pivot To Power" 
              className="w-10 h-10 rounded-full"
            />
            <span className="text-2xl font-cormorant font-bold text-white">Pivot To Power</span>
          </div>
          <div className="hidden md:flex items-center space-x-10">
            <a href="#about" className="text-gray-300 hover:text-amber-400 transition-colors font-inter text-sm">About</a>
            <a href="#results" className="text-gray-300 hover:text-amber-400 transition-colors font-inter text-sm">Results</a>
            <a href="#join" className="text-gray-300 hover:text-amber-400 transition-colors font-inter text-sm">Join</a>
            <button onClick={() => navigate('/login')} className="text-gray-300 hover:text-amber-400 transition-colors font-inter text-sm" data-testid="login-link">Login</button>
            <button onClick={() => navigate('/apply')} className="bg-gradient-to-r from-amber-400 to-pink-500 hover:from-amber-500 hover:to-pink-600 text-black font-inter font-bold px-6 py-2.5 rounded-md transition-all text-sm" data-testid="nav-join-button">
              Apply to Join
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-8 md:px-16 bg-gradient-to-br from-black via-purple-950 to-black">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <div className="inline-block mb-6">
              <span className="text-xs font-inter font-semibold text-amber-400 uppercase tracking-wider">
                By Application Only · Limited Membership
              </span>
            </div>
            
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-cormorant font-bold bg-gradient-to-r from-amber-300 via-pink-300 to-purple-300 bg-clip-text text-transparent mb-8 leading-[1.1]" data-testid="hero-headline">
              Pivot To Power Network
            </h1>
            
            <h2 className="text-3xl md:text-4xl font-cormorant font-medium text-gray-300 mb-6 leading-relaxed">
              The Most Transformative Reinvention<br />Experience on the Internet
            </h2>

            <p className="text-lg text-gray-400 font-inter max-w-2xl mx-auto mb-10 leading-relaxed">
              This is not another course. Not another community. This is the private ecosystem where serious professionals
              use AI, strategy, and elite accountability to completely reinvent their careers, businesses, and legacies.
              <span className="text-amber-400 font-semibold"> We don't accept everyone.</span>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button 
                onClick={() => navigate('/apply')}
                className="bg-gradient-to-r from-amber-400 to-pink-500 hover:from-amber-500 hover:to-pink-600 text-black font-inter font-bold px-10 py-4 rounded-md transition-all text-lg flex items-center justify-center space-x-2 shadow-lg shadow-pink-500/50"
                data-testid="hero-join-button"
              >
                <span>Apply for Membership</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <button 
                onClick={() => navigate('/login')}
                className="border-2 border-amber-400 text-amber-400 hover:bg-amber-400/10 font-inter font-medium px-10 py-4 rounded-md transition-all text-lg"
                data-testid="hero-login-button"
              >
                Member Login
              </button>
            </div>

            <p className="text-gray-500 font-inter text-sm">
              Acceptance rate: <span className="text-amber-400 font-semibold">Limited spots</span> · Reviewed within 24 hours
            </p>
          </div>

          {/* Stats Section */}
          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto py-16 border-y border-amber-500/20">
            <div className="text-center">
              <div className="text-5xl font-cormorant font-bold bg-gradient-to-r from-amber-400 to-pink-400 bg-clip-text text-transparent mb-2">94%</div>
              <div className="text-sm text-gray-400 font-inter">Report Improved Clarity</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-cormorant font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent mb-2">89%</div>
              <div className="text-sm text-gray-400 font-inter">Report Career Advancement</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-cormorant font-bold bg-gradient-to-r from-purple-400 to-amber-400 bg-clip-text text-transparent mb-2">91%</div>
              <div className="text-sm text-gray-400 font-inter">Report Income Growth</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-cormorant font-bold bg-gradient-to-r from-amber-400 to-pink-400 bg-clip-text text-transparent mb-2">9/10</div>
              <div className="text-sm text-gray-400 font-inter">Average Satisfaction Score</div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Testimonial */}
      <section className="py-20 px-8 md:px-16 bg-gradient-to-r from-pink-600 via-purple-600 to-black">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-amber-400 fill-current" />
              ))}
            </div>
            <p className="text-2xl md:text-3xl font-cormorant text-white mb-8 leading-relaxed italic" data-testid="testimonial-quote">
              "{testimonials[currentTestimonial].quote}"
            </p>
            <div className="mb-4">
              <p className="font-cormorant font-bold text-xl text-amber-300" data-testid="testimonial-name">
                {testimonials[currentTestimonial].name}
              </p>
              <p className="text-pink-200 font-inter text-sm">{testimonials[currentTestimonial].role}</p>
            </div>
            <div className="flex justify-center space-x-2 mt-8">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentTestimonial(i)}
                  className={`h-1.5 rounded-full transition-all ${
                    i === currentTestimonial ? 'w-8 bg-amber-400' : 'w-1.5 bg-pink-300/50'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 px-8 md:px-16" id="about">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-inter font-semibold text-pink-600 uppercase tracking-wider mb-4 block">
              Unlike Anything You've Seen
            </span>
            <h2 className="text-5xl md:text-6xl font-cormorant font-bold text-gray-900 mb-6 leading-tight">
              This Is Not For Everyone.<br />That's The Point.
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-inter leading-relaxed">
              Pivot To Power Network is the most exclusive reinvention ecosystem ever built. We combine
              AI-powered strategy, elite masterminds, and an unmatched private community to help serious 
              professionals build power in the next economy. If you're looking for something casual, this isn't it.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-12 text-lg text-gray-700 font-inter leading-relaxed">
            <p>
              Every member is hand-selected. Every tool is purpose-built. Every interaction is designed to
              accelerate your transformation. We built this for the professionals who are done waiting, 
              done wondering, and ready to architect their next chapter with precision and power.
            </p>
            <p>
              Through our AI-powered diagnostic, personalized Pivot Blueprint, Power Guide strategic advisor, 
              and curated Power Circles, we've created the fastest and most effective path from confusion to 
              clarity, from fear to strategic confidence, from dreaming to building.
            </p>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-24 px-8 md:px-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-inter font-semibold text-pink-600 uppercase tracking-wider mb-4 block">
              Member Benefits
            </span>
            <h2 className="text-5xl md:text-6xl font-cormorant font-bold text-gray-900 mb-6">
              What Members Receive
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                num: "01",
                title: 'Power Guide Strategic Advisor', 
                desc: 'Personalized strategic recommendations, roadmaps, and weekly check-ins tailored to your unique journey and goals.',
                gradient: 'from-amber-400 to-pink-500'
              },
              { 
                num: "02",
                title: 'Power Path Finder', 
                desc: 'Comprehensive diagnostic and onboarding that reveals your archetype, strengths, weaknesses, and personalized strategy.',
                gradient: 'from-pink-500 to-purple-600'
              },
              { 
                num: "03",
                title: 'Exclusive Power Circles', 
                desc: 'Curated mastermind groups connecting you with elite professionals navigating the same transformation.',
                gradient: 'from-purple-600 to-black'
              },
              { 
                num: "04",
                title: 'Lifestyle Designer', 
                desc: 'Strategic tools to design your ideal income, schedule, and life in the next economy.',
                gradient: 'from-amber-400 to-pink-500'
              },
              { 
                num: "05",
                title: 'Premium Academy & Resources', 
                desc: 'Exclusive courses, frameworks, and implementation tools for mastery and career transformation.',
                gradient: 'from-pink-500 to-purple-600'
              },
              { 
                num: "06",
                title: 'Private Community', 
                desc: 'Protected space for transformation with moderation that maintains quality and exclusivity.',
                gradient: 'from-purple-600 to-black'
              }
            ].map((item, i) => (
              <div key={i} className="bg-white p-8 rounded-lg border-2 border-gray-100 hover:border-pink-300 transition-all shadow-lg hover:shadow-xl">
                <div className={`text-4xl font-cormorant font-bold bg-gradient-to-r ${item.gradient} bg-clip-text text-transparent mb-4`}>{item.num}</div>
                <h3 className="text-2xl font-cormorant font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 font-inter leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Member Profiles Section */}
      <section className="py-24 px-8 md:px-16 bg-gradient-to-br from-black via-purple-950 to-black">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-inter font-semibold text-amber-400 uppercase tracking-wider mb-4 block">
              Our Community
            </span>
            <h2 className="text-5xl md:text-6xl font-cormorant font-bold bg-gradient-to-r from-amber-300 to-pink-300 bg-clip-text text-transparent mb-6">
              Meet Network Members
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-inter">
              Join elite professionals building power in the next economy
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: "Marcus J.", role: "Tech Entrepreneur", archetype: "The Builder", city: "Atlanta", photo: "https://images.unsplash.com/photo-1766939228707-d9b36da3ab06?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0JTIwaGVhZHNob3QlMjBkaXZlcnNlfGVufDB8fHx8MTc3NTg4MzEzMnww&ixlib=rb-4.1.0&q=85" },
              { name: "Amara W.", role: "Career Strategist", archetype: "The Visionary", city: "New York", photo: "https://images.unsplash.com/photo-1609436132311-e4b0c9370469?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0JTIwaGVhZHNob3QlMjBkaXZlcnNlfGVufDB8fHx8MTc3NTg4MzEzMnww&ixlib=rb-4.1.0&q=85" },
              { name: "David T.", role: "Business Consultant", archetype: "The Operator", city: "Chicago", photo: "https://images.unsplash.com/photo-1766939228707-d9b36da3ab06?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0JTIwaGVhZHNob3QlMjBkaXZlcnNlfGVufDB8fHx8MTc3NTg4MzEzMnww&ixlib=rb-4.1.0&q=85" },
              { name: "Jasmine C.", role: "Business Owner", archetype: "The Reinventor", city: "Houston", photo: "https://images.unsplash.com/photo-1609371497456-3a55a205d5eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0JTIwaGVhZHNob3QlMjBkaXZlcnNlfGVufDB8fHx8MTc3NTg4MzEzMnww&ixlib=rb-4.1.0&q=85" },
              { name: "Kenneth R.", role: "Product Leader", archetype: "The Specialist", city: "San Francisco", photo: "https://images.unsplash.com/photo-1766939228707-d9b36da3ab06?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0JTIwaGVhZHNob3QlMjBkaXZlcnNlfGVufDB8fHx8MTc3NTg4MzEzMnww&ixlib=rb-4.1.0&q=85" },
              { name: "Nia J.", role: "Marketing Director", archetype: "The Messenger", city: "Miami", photo: "https://images.unsplash.com/photo-1609371497456-3a55a205d5eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0JTIwaGVhZHNob3QlMjBkaXZlcnNlfGVufDB8fHx8MTc3NTg4MzEzMnww&ixlib=rb-4.1.0&q=85" }
            ].map((member, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm p-6 rounded-lg border border-amber-500/20 hover:border-pink-500/40 transition-all">
                <div className="mb-4">
                  <img 
                    src={member.photo} 
                    alt={member.name}
                    className="w-24 h-24 rounded-full object-cover mx-auto border-4 border-amber-500/30"
                  />
                </div>
                <h3 className="text-xl font-cormorant font-bold text-white text-center mb-1">{member.name}</h3>
                <p className="text-pink-300 text-sm text-center mb-2 font-inter">{member.role}</p>
                <div className="text-center">
                  <span className="inline-block bg-purple-900/50 border border-purple-500/30 text-purple-200 px-3 py-1 rounded-full text-xs font-inter">
                    {member.archetype}
                  </span>
                </div>
                <p className="text-gray-400 text-xs text-center mt-3 font-inter">{member.city}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-24 px-8 md:px-16 bg-gradient-to-br from-black via-purple-950 to-black" id="results">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-cormorant font-bold bg-gradient-to-r from-amber-300 to-pink-300 bg-clip-text text-transparent mb-6">
              Member Success Stories
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-inter">
              Real transformations from real members
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {successStories.map((story, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm p-8 rounded-lg border border-pink-500/20 hover:border-amber-500/40 transition-all">
                <h3 className="text-2xl font-cormorant font-bold text-white mb-2">{story.title}</h3>
                <p className="text-pink-400 font-inter font-semibold mb-4">{story.result}</p>
                <p className="text-gray-300 font-inter italic mb-4">"{story.quote}"</p>
                <p className="font-cormorant font-semibold text-amber-300">— {story.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 px-8 md:px-16 bg-white" id="join">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-inter font-semibold text-amber-600 uppercase tracking-wider mb-4 block">
              Application Required · Limited Founding Member Spots
            </span>
            <h2 className="text-5xl md:text-6xl font-cormorant font-bold text-gray-900 mb-6">
              Select Your Tier. Apply Now.
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-inter">
              Membership is by application only. We review every applicant to ensure 
              <span className="font-semibold text-gray-900"> this community stays exceptional.</span>
            </p>
          </div>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center space-x-4 mb-12">
            <span className={`font-inter text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-400'}`}>Monthly</span>
            <button onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
              className="relative w-14 h-7 bg-gray-200 rounded-full transition-colors" data-testid="billing-toggle"
              style={{ backgroundColor: billingCycle === 'annual' ? '#A855F7' : '#E5E7EB' }}>
              <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${billingCycle === 'annual' ? 'translate-x-7' : 'translate-x-0.5'}`} />
            </button>
            <span className={`font-inter text-sm font-medium ${billingCycle === 'annual' ? 'text-gray-900' : 'text-gray-400'}`}>Annual</span>
            {billingCycle === 'annual' && <span className="bg-green-100 text-green-700 text-xs font-inter font-bold px-2 py-0.5 rounded-full">Save 66%</span>}
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { 
                name: 'Starter', 
                monthlyPrice: 97,
                annualPrice: 33,
                features: ['Full Network Access', 'Power Guide AI', 'All Courses', 'Power Circles', 'Community', 'Lifestyle Designer'],
                cta: 'Apply Now',
                plan: 'monthly'
              },
              { 
                name: 'Annual', 
                monthlyPrice: 97,
                annualPrice: 33,
                badge: 'Best Value — Save 66%',
                features: ['Everything in Starter', '2 Months Free', 'Priority Support', 'Exclusive Events', 'Founding Badge', 'VIP Access'],
                cta: 'Apply Now',
                plan: 'annual',
                featured: true
              },
              { 
                name: 'Premium', 
                monthlyPrice: 297,
                annualPrice: 297,
                badge: 'Elite',
                features: ['Everything in Annual', 'Live Masterminds', '1-on-1 Strategy Calls', 'VIP Inner Circle', 'Price Lock', 'Founder Access'],
                cta: 'Apply for Elite',
                plan: 'premium'
              }
            ].map((tier, i) => {
              const price = billingCycle === 'annual' && tier.plan !== 'premium' ? tier.annualPrice : tier.monthlyPrice;
              return (
              <div 
                key={i} 
                className={`bg-white p-8 rounded-lg border-2 ${tier.featured ? 'border-pink-500 shadow-2xl shadow-pink-500/20 scale-105' : 'border-gray-200'} relative`}
                data-testid={`pricing-card-${tier.plan}`}
              >
                {tier.badge && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className={`${tier.featured ? 'bg-gradient-to-r from-pink-500 to-purple-600' : 'bg-gradient-to-r from-amber-400 to-pink-500'} text-white px-4 py-1 rounded-full text-xs font-inter font-bold`}>
                      {tier.badge}
                    </span>
                  </div>
                )}
                <h3 className="text-2xl font-cormorant font-bold text-gray-900 mb-4">{tier.name}</h3>
                <div className="mb-6">
                  <span className={`text-5xl font-cormorant font-bold bg-gradient-to-r ${tier.featured ? 'from-pink-500 to-purple-600' : 'from-gray-900 to-gray-700'} bg-clip-text text-transparent`}>${price}</span>
                  <span className="text-gray-600 font-inter">/month</span>
                  {billingCycle === 'annual' && tier.plan !== 'premium' && (
                    <p className="text-xs text-gray-400 font-inter mt-1">Billed annually at ${price * 12}/year</p>
                  )}
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, j) => (
                    <li key={j} className="flex items-start space-x-2">
                      <Check className={`w-5 h-5 ${tier.featured ? 'text-pink-500' : 'text-purple-600'} mt-0.5 flex-shrink-0`} />
                      <span className="text-gray-700 font-inter text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button 
                  onClick={() => navigate('/apply')}
                  className={`w-full ${tier.featured ? 'bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white shadow-lg shadow-pink-500/30' : 'border-2 border-gray-900 text-gray-900 hover:bg-gray-50'} font-inter font-medium py-3 rounded-md transition-all`}
                  data-testid={`pricing-cta-${tier.plan}`}
                >
                  {tier.cta}
                </button>
              </div>
            );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-8 md:px-16 bg-gradient-to-br from-black via-purple-950 to-black">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-xs font-inter font-semibold text-amber-400 uppercase tracking-wider mb-6 block">
            This Is Your Moment
          </span>
          <h2 className="text-5xl md:text-6xl font-cormorant font-bold bg-gradient-to-r from-amber-300 via-pink-300 to-purple-300 bg-clip-text text-transparent mb-8 leading-tight">
            The Future Belongs to Those<br />Who Move with Clarity
          </h2>
          <p className="text-xl text-gray-300 mb-6 font-inter leading-relaxed">
            You've seen what's possible. The question is whether you're ready to stop watching from the sidelines 
            and start building the future you know you're capable of.
          </p>
          <p className="text-lg text-gray-400 mb-12 font-inter">
            We only accept members who are <span className="text-amber-400 font-semibold">serious about transformation</span>.
            If that's you, we want to hear from you.
          </p>
          
          <button 
            onClick={() => navigate('/apply')}
            className="bg-gradient-to-r from-amber-400 to-pink-500 hover:from-amber-500 hover:to-pink-600 text-black font-inter font-bold text-lg px-12 py-4 rounded-md transition-all shadow-lg shadow-pink-500/50"
            data-testid="final-cta-button"
          >
            Apply for Membership
          </button>
          <p className="text-gray-500 font-inter text-sm mt-4">
            By application only · Reviewed within 24 hours · Limited spots available
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-amber-500/20 py-12 px-8 md:px-16">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_clarity-network-1/artifacts/b1jdxi6j_image.png" 
              alt="Pivot To Power" 
              className="w-10 h-10 rounded-full"
            />
            <span className="text-2xl font-cormorant font-bold text-white">Pivot To Power Network</span>
          </div>
          <p className="text-gray-400 font-inter text-sm">© 2025 Pivot To Power Network. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
